import { Box, Typography, Card, List, ListItem, ListItemText, IconButton, Chip } from '@mui/material';
import { MarkEmailRead as MarkReadIcon, MarkEmailUnread as MarkUnreadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCollection } from '../hooks/useFirestore';
import { db } from '../firebase';
import { doc, query as fbQuery, orderBy as fbOrderBy, updateDoc } from 'firebase/firestore';

interface Inquiry {
  id: string;
  name?: string;
  email?: string;
  companyName?: string | null;
  message?: string;
  createdAt?: any;
  isRead?: boolean;
  readAt?: any;
}

export default function InquiriesPage() {
  const navigate = useNavigate();
  const { data: inquiries } = useCollection<Inquiry>(
    'inquiries',
    (q) => fbQuery(q, fbOrderBy('createdAt', 'desc'))
  );

  const handleMarkAsRead = async (id: string) => {
    try {
      await updateDoc(doc(db, 'inquiries', id), {
        isRead: true,
        readAt: new Date(),
      });
    } catch (error) {
      console.error('Failed to mark inquiry as read:', error);
    }
  };

  const handleMarkAsUnread = async (id: string) => {
    try {
      await updateDoc(doc(db, 'inquiries', id), {
        isRead: false,
        readAt: null,
      });
    } catch (error) {
      console.error('Failed to mark inquiry as unread:', error);
    }
  };

  const unreadCount = inquiries?.filter((i) => !i.isRead).length || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">お問い合わせ</Typography>
        {unreadCount > 0 && <Chip label={`未読 ${unreadCount}件`} color="error" size="small" />}
      </Box>

      <Card>
        <List>
          {inquiries?.map((i) => (
            <ListItem
              key={i.id}
              button
              onClick={() => navigate(`/inquiries/${i.id}`)}
              sx={{
                backgroundColor: i.isRead ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                borderLeft: i.isRead ? 'none' : '4px solid #1976d2',
                '&:hover': {
                  backgroundColor: i.isRead ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.12)',
                },
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!i.isRead ? (
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(i.id);
                      }}
                      title="既読にする"
                    >
                      <MarkReadIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsUnread(i.id);
                      }}
                      title="未読に戻す"
                    >
                      <MarkUnreadIcon />
                    </IconButton>
                  )}
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: i.isRead ? 'normal' : 'bold',
                        color: i.isRead ? 'text.primary' : 'primary.main',
                      }}
                    >
                      {(i.name || '（名前未入力）') + (i.email ? ` / ${i.email}` : '')}
                    </Typography>
                    {!i.isRead && <Chip label="未読" size="small" color="error" variant="outlined" />}
                  </Box>
                }
                secondary={`${(i.companyName || '').toString() || '会社名なし'} / ${(() => {
                  const v: any = (i as any).createdAt;
                  try {
                    const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
                    return d.toLocaleString();
                  } catch {
                    return '';
                  }
                })()}`}
              />
            </ListItem>
          ))}
        </List>
      </Card>
    </Box>
  );
}


